import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { PageShell } from "@/components/page-shell";
import { ObfuscatedEmail } from "@/components/obfuscated-email";

export const Route = createFileRoute("/alliances")({
  head: () => ({
    meta: [
      { title: "Alliances — Embedded Leadership | WeVang!" },
      {
        name: "description",
        content:
          "Fractional and embedded leadership for founders and CEOs at the inflection point — inside your core, not outside advising it.",
      },
      { property: "og:title", content: "Alliances — Embedded Leadership | WeVang!" },
      {
        property: "og:description",
        content:
          "Fractional and embedded leadership for founders and CEOs at the inflection point — inside your core, not outside advising it.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://www.wevang.com/alliances" },
    ],
    links: [{ rel: "canonical", href: "https://www.wevang.com/alliances" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "WeVang! Alliances",
          serviceType: "Brand strategy consulting",
          description: "Embedded leadership: senior operators taking a seat inside your team.",
          url: "https://www.wevang.com/alliances",
          provider: {
            "@type": "Organization",
            name: "WeVang!",
            url: "https://www.wevang.com",
          },
        }),
      },
    ],
  }),
  component: AlliancesPage,
});

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-4">
      <span className="mt-[0.55rem] h-px w-10 shrink-0 bg-primary" />
      <p className="font-sans text-[14px] font-bold uppercase leading-relaxed tracking-[0.28em] text-primary">
        {children}
      </p>
    </div>
  );
}

function Section({
  children,
  watermark,
  watermarkPosition = "center",
  className = "",
}: {
  children: ReactNode;
  watermark?: string;
  watermarkPosition?: "center" | "center-bottom" | "corner-br" | "corner-bl" | "corner-tr" | "corner-tl";
  className?: string;
}) {
  const positionClasses = {
    center: "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[6.5vw]",
    "center-bottom": "left-1/2 bottom-6 -translate-x-1/2 text-[5.5vw]",
    "corner-br": "right-4 bottom-8 text-[4.5vw]",
    "corner-bl": "left-4 bottom-8 text-[4.5vw]",
    "corner-tr": "right-4 top-8 text-[4.5vw]",
    "corner-tl": "left-4 top-8 text-[4.5vw]",
  };

  return (
    <section
      className={`relative isolate overflow-hidden px-6 py-16 md:px-12 md:py-24 ${className}`}
    >
      {watermark && (
        <span
          aria-hidden
          className={`pointer-events-none absolute -z-10 hidden select-none font-display font-black uppercase leading-none tracking-tighter text-white opacity-[0.06] md:block ${positionClasses[watermarkPosition]}`}
        >
          {watermark}
        </span>
      )}
      <div className="relative mx-auto max-w-[1400px]">
        <div className="max-w-5xl">{children}</div>
      </div>
    </section>
  );
}


function EngagementCard({
  heading,
  duration,
  body,
}: {
  heading: string;
  duration: string;
  body: string;
}) {
  return (
    <article className="border border-white/20 bg-transparent p-10 transition-colors duration-250 ease-out hover:border-white/40 hover:bg-white/[0.06]">
      <h3 className="font-display text-[28px] font-extrabold leading-tight tracking-tighter text-white md:text-[32px]">
        {heading}
      </h3>
      <p className="mt-3 font-sans text-[16px] font-bold uppercase tracking-[0.18em] text-primary">
        {duration}
      </p>
      <p className="mt-6 font-sans text-[17px] font-normal leading-[1.7] text-white/80">{body}</p>
    </article>
  );
}

function AlliancesPage() {
  return (
    <PageShell overHero>
      <div className="bg-[#0A0A0A]">
        {/* HERO */}
        <section className="relative isolate overflow-hidden px-6 pb-24 pt-40 md:px-12 md:pb-32 md:pt-48">
          <div className="relative mx-auto max-w-[1400px]">
            <h1 className="max-w-5xl font-display text-[44px] font-extrabold leading-[1.02] tracking-tighter text-white md:text-[88px]">
              Beyond consulting.
              <br />
              <span className="box-decoration-clone inline-block bg-primary px-3 py-1 mt-3 text-white">
                Embedded leadership.
              </span>
            </h1>
            <p className="mt-10 max-w-2xl font-sans text-[22px] font-light leading-[1.6] text-white/80 md:text-[26px]">
              We provide embedded strategic leadership, engineering your market&apos;s future.
            </p>
          </div>
        </section>

        <div className="relative isolate flex h-[200px] items-center justify-center overflow-hidden px-6 md:h-[260px]">
          <span
            aria-hidden
            className="pointer-events-none select-none font-display text-[14vw] font-black uppercase leading-none tracking-tighter text-white opacity-[0.06]"
          >
            Alliances
          </span>
        </div>

        {/* PROBLEM > SOLUTION — part 1 */}
        <Section>
          <h2 className="font-display text-[38px] font-extrabold leading-[1.05] tracking-tighter text-white md:text-[64px]">
            Mediocrity is a strategic liability.
          </h2>
          <p className="mt-8 font-display text-[30px] font-bold uppercase leading-[1.1] tracking-tighter text-primary md:text-[52px]">
            Only leadership secures legacy.
          </p>
        </Section>

        {/* PROBLEM > SOLUTION — part 2 */}
        <Section>
          <h2 className="font-display text-[30px] font-bold leading-[1.1] tracking-tighter text-white md:text-[48px]">
            You&apos;ve defined markets.
            <br />
            You&apos;ve outmaneuvered rivals.
          </h2>
          <p className="mt-8 max-w-3xl font-sans text-[22px] font-normal leading-[1.7] text-white/80 md:text-[24px]">
            Yet, at this summit, the fight isn&apos;t for leadership — it&apos;s for{" "}
            <span className="font-bold text-primary">absolute, enduring leadership</span> amidst
            relentless disruption.
          </p>
        </Section>


        {/* WHAT IS ALLIANCES */}
        <Section>
          <Eyebrow>This is not a service</Eyebrow>
          <h2 className="mt-10 font-display text-[38px] font-extrabold leading-[1.05] tracking-tighter text-white md:text-[64px]">
            We provide embedded strategic command, engineering your market&apos;s future.
          </h2>
          <p className="mt-10 font-display text-[26px] font-bold leading-[1.2] tracking-tight text-white md:text-[40px]">
            This isn&apos;t a progression of stages.{" "}
            <span className="text-primary">This is the ultimate strategic partnership</span>{" "}
            engineered to secure your enduring leadership and future-proof your entire enterprise.
          </p>
        </Section>

        {/* WHY ALLIANCES */}
        <Section>
          <Eyebrow>We are the unseen force at your strategic core</Eyebrow>
          <h2 className="mt-10 font-display text-[30px] font-bold leading-[1.1] tracking-tighter text-white md:text-[48px]">
            You don&apos;t need another advisor.
            <br />
            You need a decision made.
          </h2>
          <p className="mt-8 max-w-3xl font-sans text-[22px] font-normal leading-[1.7] text-white/80">
            Fast, autonomous calls when speed matters. Embedded leadership when the fight is long.
            Either way, it&apos;s your next move — we just make it happen.
          </p>
        </Section>

        {/* THE SEATS WE FILL */}
        <Section>
          <Eyebrow>The seats we fill</Eyebrow>
          <h2 className="mt-10 font-display text-[28px] font-bold leading-[1.15] tracking-tighter text-white md:text-[36px]">
            Whatever the mandate, the judgment is ours<br />
            — embedded, and yours to lean on.
          </h2>
          <p className="mt-8 max-w-5xl font-sans text-[18px] font-normal leading-[1.7] text-white/80 md:text-[20px]">
            Chief of Staff (CoS) · Chief Operating Officer (COO) · Head of Strategy & Operations (BizOps) · Head of Special Projects · Head of Strategic Initiatives · Head of New Ventures · Venture Architect · Venture Lead · Head of Expansion · Head of Business Development · Head of Strategic Partnerships · Director of Product Strategy & Growth · Head of Product Operations (ProdOps) · Chief Marketing Officer (CMO) · Chief Design Officer (CDO) · Chief Creative Officer (CCO) · Chief Growth Officer (CGO)
          </p>
        </Section>


        {/* WHO IS THIS FOR */}
        <Section>
          <Eyebrow>If you are ready to command the market</Eyebrow>
          <h2 className="mt-10 font-display text-[30px] font-bold leading-[1.1] tracking-tighter text-white md:text-[48px]">
            Alliances are engineered for founders and CEOs facing the inflection point.
          </h2>
          <p className="mt-8 max-w-3xl font-sans text-[20px] font-normal leading-[1.7] text-white/80 md:text-[22px]">
            You&apos;re leading a venture-backed company (seed to Series B). You&apos;ve defined
            your market. Now you face a critical moment — product-market fit uncertainty,
            go-to-market complexity, team alignment challenges, or expansion decisions that will
            define your next era.
          </p>
          <p className="mt-10 max-w-3xl font-sans text-[24px] font-semibold leading-[1.5] text-white md:text-[28px]">
            You need more than advice. You need someone embedded in your core, thinking your
            problems, moving your business, securing your next victory.
          </p>
        </Section>


        <div className="relative isolate flex h-[200px] items-center justify-center overflow-hidden px-6 md:h-[260px]">
          <span
            aria-hidden
            className="pointer-events-none select-none font-display text-[14vw] font-black uppercase leading-none tracking-tighter text-white opacity-[0.06]"
          >
            Process
          </span>
        </div>

        {/* HOW IT WORKS */}
        <section className="relative isolate overflow-hidden px-6 py-16 md:px-12 md:py-24">
          <div className="relative mx-auto max-w-[1400px]">

            <h2 className="font-display text-[28px] font-bold leading-[1.1] tracking-tighter text-white md:text-[36px]">
              The path is clear. The process, precise.
            </h2>
            <p className="mt-6 max-w-2xl font-sans text-[18px] font-light leading-[1.7] text-white/80 md:text-[20px]">
              From tactical sprints to long-term embedded leadership — we scale to your ambition.
            </p>
            <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2">
              <EngagementCard
                heading="Short-Term Engagement"
                duration="12–20 weeks"
                body="We embed to solve a critical inflection. Product-market fit. Go-to-market strategy. Market entry. Team alignment. Weekly sync. Autonomous decision-making alongside your team. No fractional part-time advice — embedded momentum. When clarity is achieved and momentum is secured, we step back."
              />
              <EngagementCard
                heading="Long-Term Embedded Leadership"
                duration="Months or years"
                body="We join your leadership team as a strategic partner. Chief of Staff. COO. Head of Strategy & Operations. We operate as an extension of your C-suite, thinking your problems, moving your business, securing your ongoing leadership."
              />
            </div>
          </div>
        </section>


        <div className="relative isolate flex h-[200px] items-center justify-center overflow-hidden px-6 md:h-[260px]">
          <span
            aria-hidden
            className="pointer-events-none select-none font-display text-[14vw] font-black uppercase leading-none tracking-tighter text-white opacity-[0.06]"
          >
            Outcome
          </span>
        </div>

        {/* THE OUTCOME */}
        <Section>
          <Eyebrow>The Outcome</Eyebrow>
          <h2 className="mt-10 font-display text-[28px] font-bold leading-[1.15] tracking-tighter text-white md:text-[44px]">
            A company moving with stronger internal coherence, faster execution, and the strategic
            advantage that comes from having us at your core.
          </h2>
          <p className="mt-8 max-w-3xl font-sans text-[20px] font-normal leading-[1.7] text-white/80 md:text-[22px]">
            Seeing the whole system, exercising judgment under uncertainty, turning direction into
            tangible outcomes.
          </p>
        </Section>


        {/* CTA */}
        <section className="px-6 pb-28 pt-12 text-center md:px-12 md:pb-36">
          <p className="mb-4 font-sans text-sm font-light leading-relaxed text-white/80">
            We work with few companies at a time. Book your slot for the next batch.
          </p>
          <ObfuscatedEmail className="font-sans text-sm tracking-[0.3em] text-white/70 transition-colors hover:text-primary" />
        </section>
      </div>
    </PageShell>
  );
}
